import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, UGX } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState } from "../components/Common";
import { Banknote, ArrowUpRight, Receipt, CreditCard, Smartphone, Plus, Trash2, Search, Download, Calendar, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const CATEGORIES = ["Consultation","Laboratory","Pharmacy","Imaging","Procedure","Bed","Theatre","Other"];
const COLORS = ["#1976D2","#2E7D32","#F59E0B","#D32F2F","#64748B","#0EA5E9"];
const PAY_METHODS = ["MTN Mobile Money", "Airtel Money", "Card", "Cash", "Insurance"];

const summarizeCharges = (items) => {
  if (!items || items.length === 0) return { primary: "—", count: 0, categories: [] };
  const groups = {};
  for (const it of items) {
    const cat = it.category || "Other";
    groups[cat] = (groups[cat] || 0) + 1;
  }
  const cats = Object.keys(groups);
  const first = items[0];
  const primary = `${first.category || "Other"}${first.description ? ` — ${first.description}` : ""}`;
  const extra = items.length - 1;
  return { primary, count: items.length, categories: cats, extra, label: extra > 0 ? `${primary} · +${extra} more` : primary };
};

// Aging bucket helper — days past due date, categorised for the collections team
const agingOf = (inv) => {
  if (!inv.due_date) return null;
  const due = new Date(inv.due_date + "T23:59:59");
  const now = new Date();
  const diffDays = Math.floor((now - due) / 86400000);
  if (diffDays < 0) return { label: "Current", days: diffDays, tone: "slate", overdue: false };
  if (diffDays <= 30) return { label: "0–30 days", days: diffDays, tone: "green", overdue: true };
  if (diffDays <= 60) return { label: "31–60 days", days: diffDays, tone: "amber", overdue: true };
  return { label: "61–90+ days", days: diffDays, tone: "red", overdue: true };
};

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [methods, setMethods] = useState([]);
  const [claims, setClaims] = useState([]);
  const [active, setActive] = useState(null); // pay
  const [showNew, setShowNew] = useState(false);
  const [showClaim, setShowClaim] = useState(false);
  const [tab, setTab] = useState("invoices");
  const [preset, setPreset] = useState({ id: "default" });
  const [paymentsView, setPaymentsView] = useState(null);
  const [statementPatientId, setStatementPatientId] = useState(null);

  const openStatement = (patient_id) => { setStatementPatientId(patient_id); setTab("statements"); };

  const load = async () => {
    const [{data:inv},{data:s},{data:m},{data:cl}] = await Promise.all([
      api.get("/invoices"),
      api.get("/dashboard/stats"),
      api.get("/finance/payment-methods").catch(()=>({data:[]})),
      api.get("/claims").catch(()=>({data:[]})),
    ]);
    setInvoices(inv); setStats(s); setMethods(m); setClaims(cl || []);
  };
  useEffect(() => { load(); }, []);

  const unpaid = invoices.filter(i => i.status !== "Paid");
  const scrollToInvoices = () => {
    setTimeout(() => document.getElementById("open-invoices-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const applyOutstanding = () => {
    setTab("invoices");
    setPreset({ id: "outstanding", status: "open", sort: "balance-desc", ts: Date.now() });
    scrollToInvoices();
  };
  const applyUnpaidOnly = () => {
    setTab("invoices");
    setPreset({ id: "unpaid", status: "Unpaid", ts: Date.now() });
    scrollToInvoices();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader title="Billing" subtitle="Patient-facing: invoices, service charges, insurance claims, discounts, receipts, refunds & payment collection. Every paid receipt automatically feeds Finance as revenue." actions={
        <div className="flex gap-2">
          <button data-testid="new-claim-btn" onClick={() => setShowClaim(true)} className="px-4 h-10 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50"><Receipt className="w-4 h-4" /> File insurance claim</button>
          <button data-testid="new-invoice-btn" onClick={() => setShowNew(true)} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> New invoice</button>
        </div>
      } />

      <div className="border-b border-slate-200 flex gap-1">
        {[["invoices",`Invoices (${unpaid.length})`],["statements","Patient statements"],["claims",`Insurance claims (${claims.length})`]].map(([k,l])=>(
          <button key={k} data-testid={`bill-tab-${k}`} onClick={()=>setTab(k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab===k?"border-[#1976D2] text-[#1565C0]":"border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-revenue-today" onClick={() => setPaymentsView("today")} label="Revenue today" value={UGX(stats.revenue_today)} icon={Banknote} tone="green" hint="Click for today's payments" />
        <KpiCard testId="kpi-revenue-month" onClick={() => setPaymentsView("month")} label="Revenue this month" value={UGX(stats.revenue_month)} icon={ArrowUpRight} tone="blue" hint="Click for this month's payments" />
        <KpiCard testId="kpi-outstanding" onClick={applyOutstanding} label="Outstanding" value={UGX(stats.outstanding)} icon={Receipt} tone="red" hint="Click to prioritise collections" />
        <KpiCard testId="kpi-unpaid-count" onClick={applyUnpaidOnly} label="Unpaid invoices" value={unpaid.length} icon={Receipt} tone="amber" hint="Click to filter" />
      </div>

      {paymentsView && <PaymentsModal scope={paymentsView} onClose={() => setPaymentsView(null)} />}

      {tab === "invoices" && (
      <div id="open-invoices-anchor" className="space-y-6">
        <OpenInvoicesCard invoices={invoices} onPay={setActive} preset={preset} onClearPreset={() => setPreset({ id: "default", ts: Date.now() })} onOpenStatement={openStatement} />
        <SectionCard title="Payment method breakdown (MTD)" testId="payment-method-breakdown">
          <div className="p-4 h-56">
            {methods.length === 0 ? <EmptyState title="No payments yet" /> : (
              <ResponsiveContainer><PieChart>
                <Tooltip formatter={(v)=>UGX(v)} /><Legend />
                <Pie data={methods.map(m=>({name:m.method,value:m.total}))} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
                  {methods.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                </Pie>
              </PieChart></ResponsiveContainer>
            )}
          </div>
        </SectionCard>
      </div>
      )}

      {tab === "statements" && (
        <StatementsTab initialPatientId={statementPatientId} onPay={setActive} />
      )}

      {tab === "claims" && (
        <SectionCard title="Insurance claims">
          {claims.length === 0 ? <EmptyState icon={Receipt} title="No claims filed yet" description="Submit a claim to an insurance provider for pre-authorization or reimbursement." /> : (
            <table className="w-full text-sm" data-testid="claims-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-4 py-2.5">Claim no.</th><th className="text-left px-4 py-2.5">Patient</th><th className="text-left px-4 py-2.5">Provider</th><th className="text-left px-4 py-2.5">Diagnosis</th><th className="text-right px-4 py-2.5">Requested</th><th className="text-right px-4 py-2.5">Approved</th><th className="text-left px-4 py-2.5">Status</th><th className="text-right px-4 py-2.5">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {claims.map((c) => (
                  <tr key={c.claim_id} data-testid={`claim-${c.claim_id}`}>
                    <td className="px-4 py-2.5 tabular text-xs">{c.claim_no}</td>
                    <td className="px-4 py-2.5">{c.patient_name}<div className="text-xs text-slate-500 tabular">{c.patient_ths_id}</div></td>
                    <td className="px-4 py-2.5">{c.provider}<div className="text-xs text-slate-500 tabular">{c.policy_no}</div></td>
                    <td className="px-4 py-2.5">{c.diagnosis}</td>
                    <td className="px-4 py-2.5 text-right tabular">{UGX(c.requested_amount)}</td>
                    <td className="px-4 py-2.5 text-right tabular">{c.approved_amount != null ? UGX(c.approved_amount) : "—"}</td>
                    <td className="px-4 py-2.5"><span className={`chip ${c.status === "Paid" ? "chip-green" : c.status?.includes("Approved") ? "chip-blue" : c.status?.includes("Denied") ? "chip-red" : "chip-amber"}`}>{c.status}</span></td>
                    <td className="px-4 py-2.5 text-right"><ClaimActionMenu claim={c} onDone={load} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      )}

      {active && <PayModal invoice={active} onClose={()=>setActive(null)} onDone={()=>{setActive(null);load();}} />}
      {showNew && <NewInvoiceModal onClose={()=>setShowNew(false)} onDone={()=>{setShowNew(false);load();}} />}
      {showClaim && <NewClaimModal onClose={()=>setShowClaim(false)} onDone={()=>{setShowClaim(false);load();}} />}
    </div>
  );
}

function OpenInvoicesCard({ invoices, onPay, preset, onClearPreset, onOpenStatement }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("open");
  const [dateField, setDateField] = useState("due_date");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [method, setMethod] = useState("all");
  const [bucket, setBucket] = useState("all");
  const [sortMode, setSortMode] = useState("newest");
  const [chargeType, setChargeType] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);

  // Presets from KPI cards
  useEffect(() => {
    if (!preset || preset.id === "default") return;
    if (preset.status) setStatus(preset.status);
    if (preset.sort) setSortMode(preset.sort);
  }, [preset?.id, preset?.ts]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = invoices.filter((inv) => {
      // Status filter — default view is "open" (Unpaid + Partially Paid)
      if (status === "open") { if (inv.status === "Paid") return false; }
      else if (status !== "all" && inv.status !== status) return false;
      // Search by patient name / Patient ID / invoice no
      if (needle) {
        const hay = `${inv.patient_name || ""} ${inv.patient_ths_id || ""} ${inv.invoice_no || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      // Payment method filter
      if (method !== "all") {
        const list = inv.payment_methods || [];
        if (!list.includes(method)) return false;
      }
      // Date range filter (against selected field)
      const val = (inv[dateField] || "").slice(0, 10);
      if (dateFrom && val && val < dateFrom) return false;
      if (dateTo && val && val > dateTo) return false;
      // Aging bucket filter
      if (bucket !== "all") {
        const a = agingOf(inv);
        if (!a) return false;
        if (bucket === "current" && a.label !== "Current") return false;
        if (bucket === "0-30" && a.label !== "0–30 days") return false;
        if (bucket === "31-60" && a.label !== "31–60 days") return false;
        if (bucket === "61-90" && a.label !== "61–90+ days") return false;
        if (bucket === "overdue" && !a.overdue) return false;
      }
      // Charge type filter (any line item matches)
      if (chargeType !== "all") {
        const hits = (inv.items || []).some((it) => (it.category || "Other") === chargeType);
        if (!hits) return false;
      }
      return true;
    });
    // Sort
    if (sortMode === "balance-desc") rows.sort((a, b) => (b.balance || 0) - (a.balance || 0));
    else if (sortMode === "due-oldest") rows.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    // else keep newest-first (backend already sorts by created_at desc)
    return rows;
  }, [invoices, q, status, dateField, dateFrom, dateTo, method, bucket, sortMode, chargeType]);

  const filteredTotal = useMemo(() => filtered.reduce((s, i) => s + (i.balance || 0), 0), [filtered]);
  const presetActive = preset && preset.id !== "default";
  const presetLabel = preset?.id === "outstanding" ? "Outstanding invoices (balance > 0, highest first)" : preset?.id === "unpaid" ? "Unpaid invoices only" : null;

  const clearFilters = () => {
    setQ(""); setStatus("open"); setDateFrom(""); setDateTo(""); setMethod("all"); setBucket("all"); setDateField("due_date"); setSortMode("newest"); setChargeType("all");
    onClearPreset?.();
  };
  const activeFilterCount = [q, status !== "open" ? status : "", dateFrom, dateTo, method !== "all" ? method : "", bucket !== "all" ? bucket : "", sortMode !== "newest" ? sortMode : "", chargeType !== "all" ? chargeType : ""].filter(Boolean).length;

  return (
    <SectionCard
      title="Open invoices"
      testId="open-invoices-card"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 tabular" data-testid="filter-summary">{filtered.length} shown · {UGX(filteredTotal)} outstanding</span>
          {activeFilterCount > 0 && (
            <button data-testid="clear-filters" onClick={clearFilters} className="h-7 px-2 rounded-md border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-1"><X className="w-3 h-3" /> Clear ({activeFilterCount})</button>
          )}
        </div>
      }
    >
      {presetActive && (
        <div className="px-4 py-2 border-b border-blue-100 bg-blue-50/60 flex items-center justify-between gap-2" data-testid="preset-banner">
          <div className="text-xs text-[#1565C0] font-medium">Filtered from KPI: <span className="font-semibold">{presetLabel}</span></div>
          <button data-testid="preset-clear" onClick={clearFilters} className="h-7 px-2 rounded-md border border-[#1976D2]/30 bg-white text-xs font-medium text-[#1565C0] hover:bg-blue-100 flex items-center gap-1"><X className="w-3 h-3" /> Back to all invoices</button>
        </div>
      )}
      <div className="p-3 border-b border-slate-100 bg-slate-50/60 space-y-2" data-testid="invoice-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="inv-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search patient name, Patient ID or invoice no…"
              className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
            />
          </div>
          <select data-testid="inv-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="open">Status: Open (default)</option>
            <option value="Unpaid">Unpaid</option>
            <option value="Partially Paid">Partially Paid</option>
            <option value="Paid">Paid</option>
            <option value="all">All statuses</option>
          </select>
          <select data-testid="inv-method-filter" value={method} onChange={(e) => setMethod(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="all">All payment methods</option>
            {PAY_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select data-testid="inv-bucket-filter" value={bucket} onChange={(e) => setBucket(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="all">All aging</option>
            <option value="current">Current (not due)</option>
            <option value="overdue">Any overdue</option>
            <option value="0-30">0–30 days overdue</option>
            <option value="31-60">31–60 days overdue</option>
            <option value="61-90">61–90+ days overdue</option>
          </select>
          <select data-testid="inv-charge-filter" value={chargeType} onChange={(e) => setChargeType(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="all">All charge types</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-500 font-medium flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Range on:</span>
          <select data-testid="inv-date-field" value={dateField} onChange={(e) => setDateField(e.target.value)} className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white">
            <option value="due_date">Due date</option>
            <option value="created_at">Invoice date</option>
          </select>
          <input data-testid="inv-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white" />
          <span className="text-xs text-slate-400">to</span>
          <input data-testid="inv-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white" />
          <span className="text-[11px] text-slate-500 font-medium ml-2">Sort:</span>
          <select data-testid="inv-sort" value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white">
            <option value="newest">Newest first</option>
            <option value="balance-desc">Highest balance</option>
            <option value="due-oldest">Oldest due date</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices match your filters" description={activeFilterCount > 0 ? "Adjust or clear filters to widen the search." : "No open invoices right now."} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Invoice</th>
                <th className="text-left px-4 py-2.5">Patient</th>
                <th className="text-left px-4 py-2.5">Charges</th>
                <th className="text-left px-4 py-2.5">Due date</th>
                <th className="text-left px-4 py-2.5">Aging</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="text-right px-4 py-2.5">Balance</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((i) => {
                const a = agingOf(i);
                const isOpen = expandedRow === i.invoice_id;
                const s = summarizeCharges(i.items);
                return (
                  <React.Fragment key={i.invoice_id}>
                  <tr data-testid={`inv-${i.invoice_id}`} className={a?.overdue ? "bg-red-50/30" : ""}>
                    <td className="px-4 py-2.5 tabular">{i.invoice_no}{i.auto_charged && <span className="ml-1 chip chip-slate text-[10px]">auto</span>}</td>
                    <td className="px-4 py-2.5">
                      <button data-testid={`open-statement-${i.patient_id}`} onClick={() => onOpenStatement?.(i.patient_id)} className="text-left hover:underline text-[#1565C0] font-medium">
                        {i.patient_name}
                      </button>
                      <div className="text-xs text-slate-500">{i.patient_ths_id}</div>
                    </td>
                    <td className="px-4 py-2.5" data-testid={`inv-charges-${i.invoice_id}`}>
                      <div className="text-sm text-slate-800 truncate max-w-[220px]" title={s.label}>{s.label}</div>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {s.categories.slice(0, 3).map((c) => <span key={c} className="chip chip-slate text-[10px]">{c}</span>)}
                        {s.categories.length > 3 && <span className="chip chip-slate text-[10px]">+{s.categories.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs tabular">
                      {i.due_date || "—"}
                      {a?.overdue && (
                        <div className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-[#B71C1C] font-semibold" data-testid={`inv-overdue-${i.invoice_id}`}>
                          <AlertCircle className="w-3 h-3" /> Overdue
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {a ? (
                        <span data-testid={`inv-aging-${i.invoice_id}`} className={`chip chip-${a.tone}`}>{a.label}{a.overdue ? ` · ${a.days}d` : ""}</span>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular">{UGX(i.total)}</td>
                    <td className="px-4 py-2.5 text-right tabular font-semibold">{UGX(i.balance)}</td>
                    <td className="px-4 py-2.5"><StatusPill status={i.status} /></td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button data-testid={`inv-details-${i.invoice_id}`} onClick={() => setExpandedRow(isOpen ? null : i.invoice_id)} className="text-xs text-[#1565C0] font-medium hover:underline mr-2">{isOpen ? "Hide" : "Details"}</button>
                      {i.status !== "Paid" ? (
                        <button data-testid={`pay-${i.invoice_id}`} onClick={() => onPay(i)} className="px-3 h-8 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs font-semibold">Take payment</button>
                      ) : (
                        <span className="text-xs text-slate-400">Closed</span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={9} className="px-4 py-3 bg-slate-50" data-testid={`inv-lineitems-${i.invoice_id}`}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Line items ({(i.items||[]).length})</div>
                        <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden bg-white">
                          <thead className="bg-white text-[10px] uppercase text-slate-500">
                            <tr><th className="text-left px-3 py-1.5">Category</th><th className="text-left px-3 py-1.5">Description</th><th className="text-right px-3 py-1.5">Qty</th><th className="text-right px-3 py-1.5">Unit price</th><th className="text-right px-3 py-1.5">Subtotal</th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(i.items || []).map((it, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-1.5"><span className="chip chip-slate text-[10px]">{it.category || "Other"}</span></td>
                                <td className="px-3 py-1.5">{it.description || "—"}</td>
                                <td className="px-3 py-1.5 text-right tabular">{it.qty || 1}</td>
                                <td className="px-3 py-1.5 text-right tabular">{UGX(it.unit_price || 0)}</td>
                                <td className="px-3 py-1.5 text-right tabular font-semibold">{UGX((it.qty || 1) * (it.unit_price || 0))}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50">
                            <tr>
                              <td colSpan={4} className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Subtotal</td>
                              <td className="px-3 py-1.5 text-right tabular font-semibold">{UGX(i.subtotal || 0)}</td>
                            </tr>
                            {(i.discount || 0) > 0 && <tr><td colSpan={4} className="px-3 py-1 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Discount</td><td className="px-3 py-1 text-right tabular text-emerald-700">-{UGX(i.discount)}</td></tr>}
                            {(i.tax || 0) > 0 && <tr><td colSpan={4} className="px-3 py-1 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tax</td><td className="px-3 py-1 text-right tabular">{UGX(i.tax)}</td></tr>}
                            <tr>
                              <td colSpan={4} className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</td>
                              <td className="px-3 py-1.5 text-right tabular font-bold">{UGX(i.total)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function ClaimActionMenu({ claim, onDone }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (status, approved_amount = null, reason = null) => {
    setBusy(true);
    try { await api.post("/claims/status", { claim_id: claim.claim_id, status, approved_amount, reason }); toast.success(`Status → ${status}`); onDone(); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  if (claim.status === "Paid") return <span className="text-xs text-slate-400">Closed</span>;
  return (
    <select data-testid={`claim-action-${claim.claim_id}`} disabled={busy} value="" onChange={(e) => {
      const v = e.target.value;
      if (v === "approve") setStatus("Pre-auth Approved", claim.requested_amount);
      else if (v === "deny") setStatus("Pre-auth Denied", 0, "Not covered");
      else if (v === "paid") setStatus("Paid", claim.approved_amount || claim.requested_amount);
      else if (v === "query") setStatus("Query", null, "Insurer requested more info");
      e.target.value = "";
    }} className="h-8 text-xs px-2 rounded border border-slate-200 bg-white">
      <option value="">Update…</option>
      <option value="approve">Approve pre-auth</option>
      <option value="deny">Deny</option>
      <option value="paid">Mark paid</option>
      <option value="query">Query</option>
    </select>
  );
}

function PaymentsModal({ scope, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/payments");
        const now = new Date();
        const today = now.toISOString().slice(0, 10);
        const monthPrefix = now.toISOString().slice(0, 7); // YYYY-MM
        const filtered = (data || []).filter((p) => {
          const d = (p.created_at || "").slice(0, 10);
          if (scope === "today") return d === today;
          return d.startsWith(monthPrefix);
        });
        setRows(filtered);
      } catch { toast.error("Failed to load payments"); }
      finally { setLoading(false); }
    })();
  }, [scope]);

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const title = scope === "today" ? "Revenue today · payments received" : "Revenue this month · payments received";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-4xl w-full max-h-[85vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={`payments-modal-${scope}`}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{rows.length} payment{rows.length === 1 ? "" : "s"} · Total <span className="font-semibold tabular text-slate-800">{UGX(total)}</span></div>
          </div>
          <button data-testid="payments-modal-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading payments…</div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Banknote} title={scope === "today" ? "No payments recorded today" : "No payments this month yet"} />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2.5">Time</th>
                  <th className="text-left px-4 py-2.5">Patient</th>
                  <th className="text-left px-4 py-2.5">Invoice</th>
                  <th className="text-left px-4 py-2.5">Method</th>
                  <th className="text-left px-4 py-2.5">Reference</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => (
                  <tr key={p.payment_id} data-testid={`pay-row-${p.payment_id}`}>
                    <td className="px-4 py-2 text-xs tabular">{new Date(p.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-4 py-2">{p.patient_name || "—"}<div className="text-xs text-slate-500">{p.patient_ths_id}</div></td>
                    <td className="px-4 py-2 tabular text-xs">{p.invoice_no || p.invoice_id?.slice(0, 12)}</td>
                    <td className="px-4 py-2 text-xs">{p.method}</td>
                    <td className="px-4 py-2 tabular text-xs text-slate-500">{p.reference || "—"}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold">{UGX(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-white border-t border-slate-200">
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</td>
                  <td className="px-4 py-3 text-right tabular font-bold text-lg" data-testid="payments-total">{UGX(total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function NewClaimModal({ onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [provider, setProvider] = useState("UAP Old Mutual");
  const [policyNo, setPolicyNo] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [procedure, setProcedure] = useState("");
  const [amount, setAmount] = useState(0);
  useEffect(() => { if (q.length < 2) { setResults([]); return; } api.get(`/patients?q=${encodeURIComponent(q)}&limit=6`).then(r => setResults(r.data || [])); }, [q]);
  useEffect(() => { if (patient?.insurance_provider) setProvider(patient.insurance_provider); if (patient?.insurance_member) setPolicyNo(patient.insurance_member); }, [patient]);
  const submit = async () => {
    if (!patient) return toast.error("Pick a patient");
    if (!diagnosis.trim()) return toast.error("Diagnosis required");
    if (!(Number(amount) > 0)) return toast.error("Requested amount required");
    try {
      await api.post("/claims", { patient_id: patient.patient_id, provider, policy_no: policyNo, diagnosis, procedure, requested_amount: Number(amount) });
      toast.success("Claim submitted");
      onDone();
    } catch { toast.error("Failed to submit"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="new-claim-modal">
        <div className="font-semibold mb-4">File insurance claim</div>
        {!patient ? (
          <div>
            <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="claim-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient…" className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="mt-2 divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {results.map((p) => (
                <button key={p.patient_id} onClick={() => setPatient(p)} data-testid={`claim-pick-${p.patient_id}`} className="w-full text-left p-2 hover:bg-slate-50 rounded">
                  <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
                  <div className="text-xs text-slate-500 tabular">{p.ths_id} · {p.insurance_provider || "No insurance on file"}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between mb-3">
              <div><div className="font-medium text-sm">{patient.first_name} {patient.last_name}</div><div className="text-xs text-slate-500 tabular">{patient.ths_id}</div></div>
              <button onClick={() => setPatient(null)} className="text-xs text-slate-500 hover:text-slate-800">Change</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input testId="claim-provider" label="Insurance provider *" value={provider} onChange={setProvider} />
              <Input testId="claim-policy" label="Policy / member no. *" value={policyNo} onChange={setPolicyNo} />
              <Input testId="claim-diagnosis" label="Diagnosis *" value={diagnosis} onChange={setDiagnosis} />
              <Input testId="claim-procedure" label="Procedure / service" value={procedure} onChange={setProcedure} />
              <Input testId="claim-amount" label="Requested amount (UGX) *" value={amount} onChange={setAmount} />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
              <button data-testid="claim-submit" onClick={submit} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Submit claim</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatementsTab({ initialPatientId, onPay }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patientId, setPatientId] = useState(initialPatientId || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expandedInv, setExpandedInv] = useState(null);
  const [showPayAll, setShowPayAll] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => { if (initialPatientId) setPatientId(initialPatientId); }, [initialPatientId]);
  useEffect(() => {
    if (!patientId) { setData(null); return; }
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/statements/${patientId}`);
        setData(data);
      } catch { toast.error("Failed to load statement"); }
      finally { setLoading(false); }
    })();
  }, [patientId, reloadTick]);
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    api.get(`/patients?q=${encodeURIComponent(q)}&limit=8`).then((r) => setResults(r.data || [])).catch(() => setResults([]));
  }, [q]);

  const applyRange = (r) => {
    setRange(r);
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (r === "6m") { const s = new Date(now); s.setMonth(now.getMonth() - 6); setDateFrom(iso(s)); setDateTo(iso(now)); }
    else if (r === "year") { setDateFrom(`${now.getFullYear()}-01-01`); setDateTo(iso(now)); }
    else { setDateFrom(""); setDateTo(""); }
  };

  const inRange = (isoDate) => {
    const d = (isoDate || "").slice(0, 10);
    if (dateFrom && d && d < dateFrom) return false;
    if (dateTo && d && d > dateTo) return false;
    return true;
  };

  if (!patientId) {
    return (
      <SectionCard title="Patient statements" testId="statements-tab">
        <div className="p-4">
          <div className="relative max-w-md">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input data-testid="statement-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient by name or Patient ID…" autoFocus className="w-full h-10 pl-8 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
          </div>
          {results.length > 0 && (
            <ul className="mt-2 max-w-md divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
              {results.map((p) => (
                <li key={p.patient_id}>
                  <button data-testid={`statement-pick-${p.patient_id}`} onClick={() => setPatientId(p.patient_id)} className="w-full text-left p-3 hover:bg-slate-50">
                    <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
                    <div className="text-xs text-slate-500 tabular">{p.ths_id} · {p.phone_primary || "—"}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {q.length >= 2 && results.length === 0 && <div className="mt-2 text-sm text-slate-500">No patients match.</div>}
          {q.length < 2 && <EmptyState icon={Receipt} title="Search for a patient" description="Pull up a complete statement — every invoice, payment, refund and insurance claim in one place." />}
        </div>
      </SectionCard>
    );
  }

  if (loading || !data) return <SectionCard title="Patient statement"><div className="p-8 text-center text-sm text-slate-500">Loading…</div></SectionCard>;

  const invoices = (data.invoices || []).filter((inv) => inRange(inv.created_at));
  const payments = (data.payments || []).filter((p) => inRange(p.created_at));
  const rangeTotals = {
    billed: invoices.reduce((s, i) => s + (i.total || 0), 0),
    paid:   invoices.reduce((s, i) => s + (i.paid || 0), 0),
    balance:invoices.reduce((s, i) => s + (i.balance || 0), 0),
  };

  // Category totals across all in-range invoices — sum billed amount per category
  const categoryTotals = (() => {
    const acc = {};
    for (const inv of invoices) {
      for (const it of (inv.items || [])) {
        const cat = it.category || "Other";
        const line = (Number(it.qty) || 1) * (Number(it.unit_price) || 0);
        acc[cat] = (acc[cat] || 0) + line;
      }
    }
    return acc;
  })();
  const categoryEntries = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]);
  const CATEGORY_TONE = { Consultation: "chip-blue", Laboratory: "chip-green", Pharmacy: "chip-amber", Imaging: "chip-red", Procedure: "chip-slate", Bed: "chip-slate", Theatre: "chip-slate", Other: "chip-slate" };
  const rangeLabel = dateFrom || dateTo
    ? `${dateFrom || "the beginning"} → ${dateTo || "today"}`
    : (range === "6m" ? "Last 6 months" : range === "year" ? "This year" : "All time");

  // Invoices eligible for "Pay all": in range + balance > 0 + no active/pending insurance claim
  const isInsurancePending = (inv) => {
    const claim = (inv._claims || [])[0];
    if (!claim) return false;
    return !["Paid", "Denied", "Pre-auth Denied"].includes(claim.status);
  };
  const payAllInvoices = invoices.filter((i) => (i.balance || 0) > 0.01 && !isInsurancePending(i));
  const insurancePendingInvoices = invoices.filter((i) => (i.balance || 0) > 0.01 && isInsurancePending(i));
  const payAllTotal = payAllInvoices.reduce((s, i) => s + (i.balance || 0), 0);

  const printStatement = () => {
    // BULLETPROOF: render the statement into a HIDDEN IFRAME (no popup blocker, no parent
    // DOM/CSS interference), then call print() on the iframe's contentWindow. This is the
    // approach every serious "print receipt" app uses (Stripe, Shopify, etc.).
    if (!data) { toast.error("Statement is still loading"); return; }
    const money = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]));
    const invoiceRows = (invoices || []).map((inv) => {
      const paid = Number(inv.paid || 0), balance = Number(inv.balance || 0), total = Number(inv.total || 0);
      const isPaid = balance <= 0.01, isPartial = paid > 0 && balance > 0.01;
      const status = isPaid ? "PAID" : isPartial ? "PARTIALLY PAID" : "UNPAID";
      const color = isPaid ? "#1B5E20" : isPartial ? "#B45309" : "#B71C1C";
      const byCat = (inv.items || []).reduce((acc, it) => { const k = it.category || "Other"; (acc[k] = acc[k] || []).push(it); return acc; }, {});
      const cats = Object.keys(byCat).sort().map((cat) => `
        <div class="cat"><div class="cat-h">Department · ${esc(cat)}</div>
          <table class="items"><thead><tr><th>Description</th><th class="r">Qty</th><th class="r">Unit</th><th class="r">Cost</th></tr></thead>
          <tbody>${byCat[cat].map((it) => `<tr><td>${esc(it.description || "—")}</td><td class="r">${it.qty || 1}</td><td class="r">${money(it.unit_price)}</td><td class="r b">${money((it.qty || 1) * (it.unit_price || 0))}</td></tr>`).join("")}</tbody></table>
        </div>`).join("");
      return `<div class="inv">
        <div class="inv-h">
          <div><div class="b">Invoice ${esc(inv.invoice_no)}</div><div class="muted">Service date ${(inv.created_at || "").slice(0,10)}${inv.due_date ? ` · Due ${esc(inv.due_date)}` : ""}</div></div>
          <div class="r"><span class="badge" style="border-color:${color};color:${color}">${status}</span>
            <div class="muted">Total <b>${money(total)}</b> · Paid <b style="color:#1B5E20">${money(paid)}</b> · Balance <b style="color:${balance > 0 ? "#B71C1C" : "#1B5E20"}">${money(balance)}</b></div>
          </div>
        </div>${cats}
      </div>`;
    }).join("");
    const catChips = Object.entries(rangeTotals.byCat || {}).map(([k, v]) => `<span class="chip">${esc(k)}: ${money(v)}</span>`).join(" ");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Statement · ${esc(data.patient?.name || "Patient")}</title>
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; }
  body { font: 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; padding: 12mm; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .muted { color: #64748b; font-size: 11px; }
  .b { font-weight: 600; }
  .r { text-align: right; }
  hr { border: 0; border-top: 1px solid #cbd5e1; margin: 8px 0; }
  .tiles { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin: 8px 0 12px; }
  .tile { border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px 10px; }
  .tile .k { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; }
  .tile .v { font-weight: 700; font-size: 14px; }
  .chip { display: inline-block; padding: 2px 8px; border-radius: 999px; background: #f1f5f9; border: 1px solid #e2e8f0; font-size: 11px; margin-right: 4px; }
  .inv { border: 1px solid #cbd5e1; border-radius: 6px; margin-bottom: 12px; break-inside: avoid; page-break-inside: avoid; overflow: hidden; }
  .inv-h { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding: 8px 10px; background: #f8fafc; border-bottom: 1px solid #cbd5e1; }
  .badge { display: inline-block; padding: 2px 8px; border: 1.5px solid; border-radius: 3px; font-weight: 700; font-size: 10px; letter-spacing: 1px; }
  .cat { border-top: 1px solid #e2e8f0; }
  .cat-h { padding: 4px 10px; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; font-weight: 600; background: white; }
  table.items { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.items th, table.items td { padding: 4px 10px; border-top: 1px solid #f1f5f9; text-align: left; }
  table.items th { text-transform: uppercase; font-size: 9px; color: #64748b; font-weight: 600; }
  .totals { border-top: 2px solid #0f172a; margin-top: 12px; padding-top: 8px; }
  .totals table { width: 100%; border-collapse: collapse; }
  .totals td { padding: 3px 0; }
  .due { font-weight: 700; font-size: 14px; }
  .foot { border-top: 1px solid #cbd5e1; margin-top: 12px; padding-top: 6px; font-size: 9px; color: #64748b; }
  @page { margin: 10mm; }
</style></head><body>
  <h1>Vitality Medical Software — Patient statement</h1>
  <div class="b">${esc(data.patient?.name || "")}</div>
  <div class="muted">${esc(data.patient?.ths_id || "")}${data.patient?.phone_primary ? ` · ${esc(data.patient.phone_primary)}` : ""}${data.patient?.district ? ` · ${esc(data.patient.district)}` : ""}</div>
  <div class="muted">Period: ${esc(rangeLabel)}   ·   Generated: ${new Date().toLocaleString()}</div>
  <hr />
  ${catChips ? `<div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#64748b;margin-bottom:4px">Spend by category (in period)</div><div style="margin-bottom:10px">${catChips}</div>` : ""}
  <div class="tiles">
    <div class="tile"><div class="k">Total billed</div><div class="v">${money(rangeTotals.billed)}</div></div>
    <div class="tile"><div class="k">Total paid</div><div class="v" style="color:#1B5E20">${money(rangeTotals.paid)}</div></div>
    <div class="tile"><div class="k">Balance owed</div><div class="v" style="color:${rangeTotals.balance > 0 ? "#B71C1C" : "#1B5E20"}">${money(rangeTotals.balance)}</div></div>
  </div>
  ${invoiceRows || '<div class="muted" style="text-align:center;padding:20px">No invoices in this period.</div>'}
  <div class="totals">
    <table>
      <tr><td class="muted">Total billed (in period)</td><td class="r b">${money(rangeTotals.billed)}</td></tr>
      <tr><td class="muted">Total paid (partial payments made)</td><td class="r b" style="color:#1B5E20">${money(rangeTotals.paid)}</td></tr>
      <tr><td class="due">TOTAL AMOUNT DUE</td><td class="r due" style="color:${rangeTotals.balance > 0 ? "#B71C1C" : "#1B5E20"}">${money(rangeTotals.balance)}</td></tr>
    </table>
  </div>
  <div class="foot">This statement is issued by Vitality Medical Software and reflects records on file as of the generation time above. Under the Uganda Data Protection and Privacy Act 2019, this document may only be shared with authorised parties.</div>
</body></html>`;
    // Build hidden iframe (no popup blocker), write statement into it, print, then clean up.
    const prev = document.getElementById("vms-print-frame");
    if (prev) prev.remove();
    const iframe = document.createElement("iframe");
    iframe.id = "vms-print-frame";
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        const cw = iframe.contentWindow;
        cw.focus();
        cw.print();
      } catch (e) {
        console.error("iframe print failed", e);
        toast.error("Print failed. Please try again or contact support.");
      }
      // Clean up after the print dialog closes (or ~1 minute safety)
      const cleanup = () => { const el = document.getElementById("vms-print-frame"); if (el) el.remove(); };
      try { iframe.contentWindow.addEventListener("afterprint", cleanup); } catch {}
      setTimeout(cleanup, 60000);
    };
    iframe.srcdoc = html;
  };
  const takePaymentOnOldest = () => {
    const target = [...invoices].reverse().find((i) => (i.balance || 0) > 0.01);
    if (target) onPay?.(target); else toast.info("No outstanding balance to pay");
  };

  return (
    <SectionCard title="Patient statement" testId="statements-tab" actions={
      <button data-testid="statement-back" onClick={() => { setPatientId(null); setData(null); setQ(""); }} className="h-8 px-3 rounded-md border border-slate-200 text-xs font-medium hover:bg-slate-50 flex items-center gap-1">
        <X className="w-3 h-3" /> Change patient
      </button>
    }>
      {/* Identity + total balance header */}
      <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-2xl font-semibold text-slate-900" data-testid="statement-name">{data.patient.name}</div>
          <div className="text-sm text-slate-500 tabular mt-0.5">{data.patient.ths_id}{data.patient.phone ? ` · ${data.patient.phone}` : ""}{data.patient.district ? ` · ${data.patient.district}` : ""}</div>
          {data.patient.insurance_provider && <div className="text-xs text-[#1565C0] mt-1">Insurance: {data.patient.insurance_provider}{data.patient.insurance_member ? ` · ${data.patient.insurance_member}` : ""}</div>}
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total balance owed</div>
          <div className={`text-3xl font-bold tabular ${data.totals.balance > 0 ? "text-[#B71C1C]" : "text-[#1B5E20]"}`} data-testid="statement-balance">{UGX(data.totals.balance)}</div>
          <div className="text-xs text-slate-500 tabular mt-0.5">Across {data.totals.invoice_count} invoice{data.totals.invoice_count === 1 ? "" : "s"} · billed {UGX(data.totals.billed)} · paid {UGX(data.totals.paid)}</div>
        </div>
      </div>

      {/* Range filter + actions */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between gap-2 flex-wrap no-print">
        <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-0.5 bg-white">
          {[["6m","Last 6 months"],["year","This year"],["all","All time"]].map(([k,l]) => (
            <button key={k} data-testid={`range-${k}`} onClick={() => applyRange(k)} className={`px-2 h-7 text-xs font-medium rounded-md ${range===k?"bg-[#1976D2] text-white":"text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
          <input data-testid="range-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="ml-1 h-7 px-2 rounded-md border border-slate-200 text-xs" />
          <span className="text-xs text-slate-400 px-1">to</span>
          <input data-testid="range-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 px-2 rounded-md border border-slate-200 text-xs" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 tabular">In range: <span className="font-semibold">{UGX(rangeTotals.balance)}</span> outstanding</span>
          <button data-testid="statement-print" onClick={printStatement} className="h-8 px-3 rounded-lg border border-slate-200 text-xs font-medium hover:bg-slate-50 flex items-center gap-1"><Download className="w-3 h-3" /> Print / PDF</button>
          {data.totals.balance > 0 && (
            <button data-testid="statement-take-payment" onClick={takePaymentOnOldest} className="h-8 px-3 rounded-lg border border-[#2E7D32] text-[#1B5E20] hover:bg-emerald-50 text-xs font-semibold">Take payment (oldest)</button>
          )}
          {payAllTotal > 0.01 && (
            <button data-testid="statement-pay-all" onClick={() => setShowPayAll(true)} className="h-8 px-3 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs font-semibold">
              Pay all ({UGX(payAllTotal)})
            </button>
          )}
        </div>
      </div>

      {showPayAll && (
        <PayAllModal
          patient={data.patient}
          invoices={payAllInvoices}
          insurancePending={insurancePendingInvoices}
          onClose={() => setShowPayAll(false)}
          onDone={(rcpt) => { setShowPayAll(false); setReceipt(rcpt); setReloadTick((t) => t + 1); }}
        />
      )}
      {receipt && <BulkReceiptModal receipt={receipt} patient={data.patient} onClose={() => setReceipt(null)} />}

      {/* Category summary — spend by charge type across in-range invoices */}
      {categoryEntries.length > 0 && (
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap" data-testid="statement-category-summary">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Spend by category</span>
          {categoryEntries.map(([cat, amt]) => (
            <span key={cat} data-testid={`cat-sum-${cat.toLowerCase()}`} className={`chip ${CATEGORY_TONE[cat] || "chip-slate"} tabular`}>
              {cat}: <span className="font-semibold ml-0.5">{UGX(amt)}</span>
            </span>
          ))}
          <span className="ml-auto text-[11px] tabular text-slate-500">Range: <span className="font-semibold text-slate-700">{rangeLabel}</span></span>
        </div>
      )}

      {/* Invoice list */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5">Invoice</th>
              <th className="text-left px-4 py-2.5">Charges</th>
              <th className="text-left px-4 py-2.5">Service date</th>
              <th className="text-left px-4 py-2.5">Due</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-right px-4 py-2.5">Paid</th>
              <th className="text-right px-4 py-2.5">Balance</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-left px-4 py-2.5">Claim</th>
              <th className="text-right px-4 py-2.5 no-print">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.length === 0 ? (
              <tr><td colSpan={10} className="p-6 text-center text-sm text-slate-500">No invoices in this range.</td></tr>
            ) : invoices.map((inv) => {
              const claim = (inv._claims || [])[0];
              const isOpen = expandedInv === inv.invoice_id;
              const s = summarizeCharges(inv.items);
              return (
                <React.Fragment key={inv.invoice_id}>
                  <tr data-testid={`stmt-inv-${inv.invoice_id}`}>
                    <td className="px-4 py-2.5 tabular">{inv.invoice_no}</td>
                    <td className="px-4 py-2.5">
                      <div className="text-xs text-slate-700">{s.label}</div>
                      {s.categories.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {s.categories.slice(0, 4).map((c) => <span key={c} className={`chip ${CATEGORY_TONE[c] || "chip-slate"} text-[10px]`}>{c}</span>)}
                          {s.categories.length > 4 && <span className="chip chip-slate text-[10px]">+{s.categories.length - 4}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 tabular text-xs">{(inv.created_at || "").slice(0,10)}</td>
                    <td className="px-4 py-2.5 tabular text-xs">{inv.due_date || "—"}</td>
                    <td className="px-4 py-2.5 text-right tabular">{UGX(inv.total)}</td>
                    <td className="px-4 py-2.5 text-right tabular text-[#1B5E20]">{UGX(inv.paid || 0)}</td>
                    <td className="px-4 py-2.5 text-right tabular font-semibold">{UGX(inv.balance)}</td>
                    <td className="px-4 py-2.5"><StatusPill status={inv.status} /></td>
                    <td className="px-4 py-2.5 text-xs">{claim ? <span className={`chip ${claim.status?.includes("Paid") ? "chip-green" : claim.status?.includes("Approved") ? "chip-blue" : claim.status?.includes("Denied") ? "chip-red" : "chip-amber"}`}>{claim.status}</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap no-print">
                      <button data-testid={`statement-expand-${inv.invoice_id}`} onClick={() => setExpandedInv(isOpen ? null : inv.invoice_id)} className="text-xs text-[#1565C0] font-medium hover:underline mr-2">{isOpen ? "Hide" : "Details"}</button>
                      {inv.balance > 0 && <button onClick={() => onPay?.(inv)} className="px-2.5 h-7 rounded-md bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-xs font-semibold">Pay</button>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={10} className="px-4 py-3 bg-slate-50" data-testid={`stmt-lineitems-${inv.invoice_id}`}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Line items ({(inv.items || []).length})</div>
                            <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden bg-white">
                              <thead className="bg-white text-[10px] uppercase text-slate-500">
                                <tr><th className="text-left px-3 py-1.5">Category</th><th className="text-left px-3 py-1.5">Description</th><th className="text-right px-3 py-1.5">Qty</th><th className="text-right px-3 py-1.5">Unit price</th><th className="text-right px-3 py-1.5">Subtotal</th></tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {(inv.items || []).map((it, idx) => (
                                  <tr key={idx}>
                                    <td className="px-3 py-1.5"><span className={`chip ${CATEGORY_TONE[it.category] || "chip-slate"} text-[10px]`}>{it.category || "Other"}</span></td>
                                    <td className="px-3 py-1.5">{it.description || "—"}</td>
                                    <td className="px-3 py-1.5 text-right tabular">{it.qty || 1}</td>
                                    <td className="px-3 py-1.5 text-right tabular">{UGX(it.unit_price || 0)}</td>
                                    <td className="px-3 py-1.5 text-right tabular font-semibold">{UGX((it.qty || 1) * (it.unit_price || 0))}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-slate-50">
                                <tr><td colSpan={4} className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Subtotal</td><td className="px-3 py-1.5 text-right tabular font-semibold">{UGX(inv.subtotal || 0)}</td></tr>
                                {(inv.discount || 0) > 0 && <tr><td colSpan={4} className="px-3 py-1 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Discount</td><td className="px-3 py-1 text-right tabular text-emerald-700">-{UGX(inv.discount)}</td></tr>}
                                {(inv.tax || 0) > 0 && <tr><td colSpan={4} className="px-3 py-1 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tax</td><td className="px-3 py-1 text-right tabular">{UGX(inv.tax)}</td></tr>}
                                <tr><td colSpan={4} className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</td><td className="px-3 py-1.5 text-right tabular font-bold">{UGX(inv.total)}</td></tr>
                              </tfoot>
                            </table>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Payments applied</div>
                            {(inv._payments || []).length === 0 ? <div className="text-xs text-slate-500 rounded-md border border-dashed border-slate-200 bg-white p-2.5">No payments yet.</div> : (
                              <ul className="text-xs space-y-0.5 rounded-md border border-slate-200 bg-white p-2">
                                {(inv._payments || []).map((p) => (
                                  <li key={p.payment_id} className="flex justify-between tabular"><span>{(p.created_at || "").slice(0,10)} · {p.method}{p.reference ? ` · ${p.reference}` : ""}</span><span className="text-[#1B5E20] font-semibold">{UGX(p.amount)}</span></li>
                                ))}
                              </ul>
                            )}
                            {claim && (
                              <>
                                <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Insurance claim</div>
                                <div className="text-xs tabular rounded-md border border-slate-200 bg-white p-2">{claim.claim_no} · {claim.provider} · {claim.status} · req {UGX(claim.requested_amount)}{claim.approved_amount != null ? ` · appr ${UGX(claim.approved_amount)}` : ""}</div>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100">
            <tr>
              <td colSpan={4} className="px-4 py-2.5 text-right text-[11px] uppercase font-semibold text-slate-500">In-range totals</td>
              <td className="px-4 py-2.5 text-right tabular font-semibold">{UGX(rangeTotals.billed)}</td>
              <td className="px-4 py-2.5 text-right tabular font-semibold text-[#1B5E20]">{UGX(rangeTotals.paid)}</td>
              <td className="px-4 py-2.5 text-right tabular font-bold" data-testid="range-balance">{UGX(rangeTotals.balance)}</td>
              <td colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Print-only itemized statement — rendered via a React portal to <body> so the
          @media print CSS can cleanly hide the app root without any ancestor's
          overflow/transform clipping the printed page. */}
      {createPortal(
      <div className="print-only px-6 py-4" data-testid="statement-print-block">
        <div className="mb-4 pb-3 border-b border-slate-300">
          <div className="text-lg font-bold">Vitality Medical Software — Patient statement</div>
          <div className="text-xs text-slate-600 mt-1">
            <span className="font-semibold">{data.patient.name}</span> · {data.patient.ths_id}
            {data.patient.phone ? ` · ${data.patient.phone}` : ""}
            {data.patient.district ? ` · ${data.patient.district}` : ""}
          </div>
          {data.patient.insurance_provider && <div className="text-xs text-slate-600">Insurance: {data.patient.insurance_provider}{data.patient.insurance_member ? ` · Member ${data.patient.insurance_member}` : ""}</div>}
          <div className="text-[11px] text-slate-500 mt-2 flex flex-wrap gap-3">
            <span>Period: <span className="font-semibold tabular">{rangeLabel}</span></span>
            <span>Generated: <span className="tabular">{new Date().toLocaleString()}</span></span>
          </div>
        </div>

        {categoryEntries.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Spend by category (in period)</div>
            <div className="flex flex-wrap gap-2 text-xs">
              {categoryEntries.map(([cat, amt]) => (
                <span key={cat} className="tabular border border-slate-300 rounded px-2 py-0.5">{cat}: <span className="font-semibold">{UGX(amt)}</span></span>
              ))}
            </div>
          </div>
        )}

        <div className="mb-4 grid grid-cols-3 gap-3 text-xs">
          <div className="border border-slate-300 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total billed</div><div className="tabular font-bold text-sm">{UGX(rangeTotals.billed)}</div></div>
          <div className="border border-slate-300 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total paid</div><div className="tabular font-bold text-sm text-emerald-700">{UGX(rangeTotals.paid)}</div></div>
          <div className="border border-slate-300 rounded p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Balance owed</div><div className={`tabular font-bold text-sm ${rangeTotals.balance > 0 ? "text-[#B71C1C]" : "text-emerald-700"}`}>{UGX(rangeTotals.balance)}</div></div>
        </div>

        {invoices.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No invoices in this period.</div>
        ) : invoices.map((inv) => {
          const paid = Number(inv.paid || 0);
          const balance = Number(inv.balance || 0);
          const total = Number(inv.total || 0);
          const isPaid = balance <= 0.01;
          const isPartial = paid > 0 && balance > 0.01;
          const statusLabel = isPaid ? "PAID" : isPartial ? "PARTIALLY PAID" : "UNPAID";
          const statusColor = isPaid ? "#1B5E20" : isPartial ? "#B45309" : "#B71C1C";
          // Group items by department/category for a clearer statement
          const byCat = (inv.items || []).reduce((acc, it) => {
            const k = it.category || "Other";
            (acc[k] = acc[k] || []).push(it);
            return acc;
          }, {});
          const cats = Object.keys(byCat).sort();
          return (
          <div key={inv.invoice_id} className="mb-4 print-avoid-break border border-slate-300 rounded">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-300 flex justify-between items-start text-xs gap-3">
              <div className="tabular">
                <div><span className="font-semibold">Invoice {inv.invoice_no}</span></div>
                <div className="text-slate-600">Service date {(inv.created_at || "").slice(0,10)}{inv.due_date ? ` · Due ${inv.due_date}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="inline-block px-2 py-0.5 rounded font-bold text-[10px] tracking-wider" style={{border: `1.5px solid ${statusColor}`, color: statusColor}}>{statusLabel}</div>
                <div className="tabular mt-1"><span className="font-semibold">Total</span> {UGX(total)} · <span className="font-semibold">Paid</span> <span style={{color: "#1B5E20"}}>{UGX(paid)}</span> · <span className="font-semibold">Balance</span> <span style={{color: balance > 0 ? "#B71C1C" : "#1B5E20", fontWeight: 700}}>{UGX(balance)}</span></div>
              </div>
            </div>
            {cats.map((cat) => (
              <div key={cat} className="border-t border-slate-200">
                <div className="px-3 py-1 bg-white text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Department · {cat}</div>
                <table className="w-full text-[11px]">
                  <thead className="text-[9px] uppercase text-slate-500 bg-white">
                    <tr><th className="text-left px-3 py-1">Description</th><th className="text-right px-3 py-1">Qty</th><th className="text-right px-3 py-1">Unit</th><th className="text-right px-3 py-1">Cost</th></tr>
                  </thead>
                  <tbody>
                    {byCat[cat].map((it, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-3 py-1">{it.description || "—"}</td>
                        <td className="px-3 py-1 text-right tabular">{it.qty || 1}</td>
                        <td className="px-3 py-1 text-right tabular">{UGX(it.unit_price || 0)}</td>
                        <td className="px-3 py-1 text-right tabular font-semibold">{UGX((it.qty || 1) * (it.unit_price || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        );})}

        {/* Grand summary at the foot of the statement */}
        <div className="mt-4 border-t-2 border-slate-800 pt-3">
          <table className="w-full text-xs">
            <tbody>
              <tr><td className="py-1 text-slate-600">Total billed (in period)</td><td className="py-1 text-right tabular font-semibold">{UGX(rangeTotals.billed)}</td></tr>
              <tr><td className="py-1 text-slate-600">Total paid (partial payments made)</td><td className="py-1 text-right tabular font-semibold" style={{color: "#1B5E20"}}>{UGX(rangeTotals.paid)}</td></tr>
              <tr className="border-t border-slate-400"><td className="py-1.5 font-bold">TOTAL AMOUNT DUE</td><td className="py-1.5 text-right tabular font-bold text-base" style={{color: rangeTotals.balance > 0 ? "#B71C1C" : "#1B5E20"}}>{UGX(rangeTotals.balance)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-[10px] text-slate-500 border-t border-slate-300 pt-2">
          This statement is issued by Vitality Medical Software and reflects records on file as of the generation time above.
          Under the Uganda Data Protection and Privacy Act 2019, this document may only be shared with authorised parties.
        </div>
      </div>,
      document.body)}

      {/* Refunds / write-offs */}
      {(data.refunds || []).length > 0 && (
        <div className="p-4 border-t border-slate-200">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Refunds & write-offs</div>
          <ul className="text-sm space-y-1">
            {data.refunds.map((r) => (
              <li key={r.refund_id} className="flex justify-between tabular"><span>{(r.created_at || "").slice(0,10)} · {r.reason}</span><span className="text-[#B71C1C] font-semibold">-{UGX(r.amount)}</span></li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

function PayAllModal({ patient, invoices, insurancePending, onClose, onDone }) {
  const total = invoices.reduce((s, i) => s + (i.balance || 0), 0);
  const [amount, setAmount] = useState(total);
  const [method, setMethod] = useState("MTN Mobile Money");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  // Preview allocation oldest-first
  const preview = (() => {
    const oldest = [...invoices].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    let remaining = Number(amount) || 0;
    return oldest.map((inv) => {
      const apply = Math.min(remaining, inv.balance || 0);
      remaining = Math.max(0, remaining - apply);
      return { inv, apply, newBalance: (inv.balance || 0) - apply };
    });
  })();
  const applied = preview.reduce((s, r) => s + r.apply, 0);

  const confirm = async () => {
    if (!amount || amount <= 0) { toast.error("Enter an amount"); return; }
    if (invoices.length === 0) { toast.error("No eligible invoices"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/bulk", {
        patient_id: patient.patient_id,
        invoice_ids: invoices.map((i) => i.invoice_id),
        amount: Number(amount),
        method,
        reference: reference || undefined,
      });
      toast.success(`Applied ${UGX(data.amount_applied)} across ${data.invoice_count} invoice${data.invoice_count === 1 ? "" : "s"}`);
      onDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to take bulk payment");
    } finally { setBusy(false); }
  };

  const PAY_METHODS = ["Cash", "MTN Mobile Money", "Airtel Money", "Card", "Bank transfer", "Insurance"];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="pay-all-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Pay all — {patient.name}</div>
            <div className="text-xs text-slate-500 tabular mt-0.5">{patient.ths_id} · {invoices.length} invoice{invoices.length === 1 ? "" : "s"} totaling {UGX(total)}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Invoices included ({invoices.length})</div>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr><th className="text-left px-3 py-2">Invoice</th><th className="text-left px-3 py-2">Date</th><th className="text-right px-3 py-2">Balance</th><th className="text-right px-3 py-2">Will apply</th><th className="text-right px-3 py-2">After</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.map(({ inv, apply, newBalance }) => (
                    <tr key={inv.invoice_id} data-testid={`payall-line-${inv.invoice_id}`}>
                      <td className="px-3 py-1.5 tabular">{inv.invoice_no}</td>
                      <td className="px-3 py-1.5 tabular text-xs">{(inv.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-1.5 text-right tabular">{UGX(inv.balance)}</td>
                      <td className="px-3 py-1.5 text-right tabular font-semibold text-[#1B5E20]">{apply > 0 ? UGX(apply) : "—"}</td>
                      <td className="px-3 py-1.5 text-right tabular text-slate-500">{UGX(newBalance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100">
                  <tr>
                    <td colSpan={2} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total</td>
                    <td className="px-3 py-2 text-right tabular font-semibold">{UGX(total)}</td>
                    <td className="px-3 py-2 text-right tabular font-bold text-[#1B5E20]" data-testid="payall-applied-preview">{UGX(applied)}</td>
                    <td className="px-3 py-2 text-right tabular">{UGX(total - applied)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {insurancePending.length > 0 && (
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs">
              <div className="font-semibold text-[#1565C0] mb-1">Insurance-pending invoices excluded ({insurancePending.length})</div>
              <ul className="space-y-0.5">
                {insurancePending.map((inv) => (
                  <li key={inv.invoice_id} className="flex justify-between tabular text-slate-700"><span>{inv.invoice_no} · claim {inv._claims?.[0]?.claim_no}</span><span>{UGX(inv.balance)} · {inv._claims?.[0]?.status}</span></li>
                ))}
              </ul>
              <div className="text-slate-500 mt-1">These balances may be paid by the insurer — settle them individually once the claim is resolved.</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Amount to collect</div>
              <div className="flex items-center gap-2">
                <input data-testid="payall-amount" type="number" min="0" step="100" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
                <button data-testid="payall-full" onClick={() => setAmount(total)} className="h-10 px-3 rounded-lg border border-slate-200 text-xs font-medium hover:bg-slate-50 whitespace-nowrap">Full ({UGX(total)})</button>
              </div>
              {amount > total && <div className="text-[11px] text-amber-700 mt-1">Amount exceeds outstanding — the extra {UGX(amount - total)} will be returned as change on the receipt.</div>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Payment method</div>
              <select data-testid="payall-method" value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                {PAY_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Reference (optional)</div>
              <input data-testid="payall-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="MoMo TxnID / cheque no / card auth code…" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-500">Applies oldest invoice first · fully audited</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-9 px-3 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="payall-confirm" onClick={confirm} disabled={busy || amount <= 0 || invoices.length === 0} className="h-9 px-4 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50">
              {busy ? "Processing…" : `Confirm ${UGX(amount)} · ${method}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkReceiptModal({ receipt, patient, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-receipt-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between no-print">
          <div className="font-semibold text-slate-900">Payment receipt</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 print-area">
          <div className="text-center mb-4">
            <div className="text-lg font-bold">Consolidated payment receipt</div>
            <div className="text-xs text-slate-500 tabular" data-testid="receipt-no">{receipt.receipt_no}</div>
          </div>
          <div className="border-t border-b border-slate-200 py-3 mb-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Patient</span><span className="font-medium">{patient.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Patient ID</span><span className="tabular">{patient.ths_id}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="tabular">{new Date(receipt.created_at).toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Method</span><span>{receipt.method}</span></div>
            {receipt.reference && <div className="flex justify-between"><span className="text-slate-500">Reference</span><span className="tabular">{receipt.reference}</span></div>}
            <div className="flex justify-between"><span className="text-slate-500">Cashier</span><span>{receipt.cashier_name}</span></div>
          </div>

          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Allocation across {receipt.invoice_count} invoice{receipt.invoice_count === 1 ? "" : "s"}</div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-slate-500">
              <tr><th className="text-left py-1">Invoice</th><th className="text-left py-1">Date</th><th className="text-right py-1">Applied</th><th className="text-right py-1">Remaining</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(receipt.allocations || []).map((a) => (
                <tr key={a.payment_id}>
                  <td className="py-1 tabular">{a.invoice_no}</td>
                  <td className="py-1 tabular text-xs">{a.invoice_date}</td>
                  <td className="py-1 text-right tabular font-semibold">{UGX(a.applied)}</td>
                  <td className="py-1 text-right tabular text-slate-500">{UGX(a.new_balance)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-300">
              <tr>
                <td colSpan={2} className="py-2 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total applied</td>
                <td className="py-2 text-right tabular font-bold text-[#1B5E20]" data-testid="receipt-applied">{UGX(receipt.amount_applied)}</td>
                <td></td>
              </tr>
              {receipt.change > 0.01 && (
                <tr>
                  <td colSpan={2} className="py-1 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Change / credit</td>
                  <td className="py-1 text-right tabular font-semibold text-amber-700">{UGX(receipt.change)}</td>
                  <td></td>
                </tr>
              )}
              <tr>
                <td colSpan={2} className="py-1 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Tendered</td>
                <td className="py-1 text-right tabular">{UGX(receipt.amount_tendered)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>

          <div className="mt-6 text-center text-xs text-slate-500">
            Thank you. Please keep this receipt for your records.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 no-print">
          <button onClick={onClose} className="h-9 px-3 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Close</button>
          <button data-testid="receipt-print" onClick={() => window.print()} className="h-9 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Print / PDF</button>
        </div>
      </div>
    </div>
  );
}

function PayModal({ invoice, onClose, onDone }) {
  const [method, setMethod] = useState("MTN Mobile Money");
  const [amount, setAmount] = useState(invoice.balance);
  const [reference, setReference] = useState("");
  const [phone, setPhone] = useState(invoice.patient_phone || "+256");
  const [card, setCard] = useState("");
  const [signature, setSignature] = useState("");
  // Mobile Money push-to-phone state
  const isMoMo = method === "MTN Mobile Money" || method === "Airtel Money";
  const [pushMode, setPushMode] = useState(true);
  const [momoTxn, setMomoTxn] = useState(null); // {txn_id, status, provider, reference, failure_reason}

  // Poll MoMo status while pending
  useEffect(() => {
    if (!momoTxn || momoTxn.status !== "pending") return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/momo/status/${momoTxn.txn_id}`);
        if (!alive) return;
        setMomoTxn(data);
        if (data.status === "success") {
          toast.success(`Paid · ${data.reference}`);
          setTimeout(() => onDone(), 900);
        } else if (["failed", "timeout", "cancelled"].includes(data.status)) {
          toast.error(`Payment ${data.status}${data.failure_reason ? `: ${data.failure_reason}` : ""}`);
        }
      } catch (e) { console.debug("MoMo poll failed", e); }
    };
    const id = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(id); };
  }, [momoTxn, onDone]);

  const submitPush = async () => {
    const provider = method === "MTN Mobile Money" ? "mtn" : "airtel";
    try {
      const { data } = await api.post("/momo/request-to-pay", {
        invoice_id: invoice.invoice_id, msisdn: phone, amount: Number(amount), provider,
      });
      setMomoTxn(data);
      toast.message(`Push sent to ${data.msisdn} — customer must approve on their phone`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send push"); }
  };
  const cancelPush = async () => {
    if (!momoTxn) return;
    try { await api.post("/momo/cancel", { txn_id: momoTxn.txn_id }); setMomoTxn({ ...momoTxn, status: "cancelled" }); }
    catch (e) { console.debug("cancel failed", e); }
  };
  const submitManual = async () => {
    if (!reference.trim()) return toast.error("Transaction ID / receipt reference is required as proof of payment");
    if (!signature.trim()) return toast.error("Electronic signature required for validation");
    try {
      const { data } = await api.post("/payments", { invoice_id: invoice.invoice_id, method, amount: Number(amount), reference, phone, card_last4: card.slice(-4), signature });
      toast.success(`Payment recorded · ${data.payment.reference}`);
      onDone();
    } catch { toast.error("Payment failed"); }
  };
  const submit = () => { (isMoMo && pushMode) ? submitPush() : submitManual(); };

  // Pending / success UI takes over once we've kicked off a push
  if (momoTxn && momoTxn.status === "pending") {
    return (
      <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="momo-pending">
          <div className="flex items-center justify-between mb-2">
            <div><div className="text-sm text-slate-500">Waiting for approval · {invoice.invoice_no}</div><div className="font-semibold">{invoice.patient_name}</div></div>
            <span className={`chip ${momoTxn.provider === "mtn" ? "chip-amber" : "chip-red"}`}>{momoTxn.provider === "mtn" ? "MTN MoMo" : "Airtel Money"}</span>
          </div>
          <div className="my-6 flex flex-col items-center">
            <div className="relative w-20 h-20 mb-4">
              <div className="absolute inset-0 rounded-full border-4 border-slate-100" />
              <div className="absolute inset-0 rounded-full border-4 border-[#1976D2] border-t-transparent animate-spin" />
              <Smartphone className="absolute inset-0 m-auto w-8 h-8 text-[#1976D2]" />
            </div>
            <div className="text-lg font-semibold tabular">{UGX(momoTxn.amount)}</div>
            <div className="text-sm text-slate-600 tabular mt-1">{momoTxn.msisdn}</div>
            <div className="text-xs text-slate-500 mt-3 text-center">Customer must approve the request on their phone. This dialog auto-closes when payment completes.</div>
            {momoTxn.mode === "demo" && <div className="text-[10px] uppercase tracking-widest text-amber-700 mt-2">Demo mode · will auto-approve in ~6s</div>}
          </div>
          <div className="flex justify-end gap-2">
            <button data-testid="momo-cancel" onClick={cancelPush} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel request</button>
          </div>
        </div>
      </div>
    );
  }
  if (momoTxn && momoTxn.status === "success") {
    return (
      <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onDone}>
        <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="momo-success">
          <div className="my-6 flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-3"><Banknote className="w-8 h-8 text-emerald-600" /></div>
            <div className="text-lg font-semibold">Payment successful</div>
            <div className="text-sm text-slate-600 tabular mt-1">{UGX(momoTxn.amount)} · {momoTxn.msisdn}</div>
            <div className="text-xs text-slate-500 tabular mt-2">Ref: {momoTxn.reference}</div>
          </div>
          <div className="flex justify-end">
            <button onClick={onDone} className="px-5 h-10 rounded-lg bg-[#1976D2] text-white text-sm font-semibold">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={e=>e.stopPropagation()} data-testid="pay-modal">
        <div className="flex items-center justify-between mb-2">
          <div><div className="text-sm text-slate-500">Payment · {invoice.invoice_no}</div><div className="font-semibold">{invoice.patient_name}</div></div>
          <span className="chip chip-amber">DEMO</span>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between mb-4">
          <span className="text-sm text-slate-600">Balance</span><span className="text-xl font-bold tabular">{UGX(invoice.balance)}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[["MTN Mobile Money",Smartphone],["Airtel Money",Smartphone],["Card",CreditCard],["Cash",Banknote],["Insurance",Receipt]].map(([m,Icon]) => (
            <button key={m} data-testid={`method-${m.replace(/\s/g,'-').toLowerCase()}`} onClick={()=>setMethod(m)} className={`p-2 rounded-lg border text-xs font-medium flex flex-col items-center gap-1 ${method===m?"border-[#1976D2] bg-[#e6f0fb] text-[#1565C0]":"border-slate-200 hover:bg-slate-50"}`}><Icon className="w-4 h-4" /> {m.split(" ")[0]}</button>
          ))}
        </div>
        {isMoMo && (
          <div className="rounded-lg border border-slate-200 p-2 mb-3 flex gap-1">
            <button data-testid="momo-mode-push" onClick={() => setPushMode(true)} className={`flex-1 h-8 rounded-md text-xs font-semibold ${pushMode ? "bg-[#1976D2] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Push to phone</button>
            <button data-testid="momo-mode-manual" onClick={() => setPushMode(false)} className={`flex-1 h-8 rounded-md text-xs font-semibold ${!pushMode ? "bg-[#1976D2] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Manual reference</button>
          </div>
        )}
        {isMoMo && <Input testId="pay-phone" label="Customer phone" value={phone} onChange={setPhone} placeholder="0771 234 567 or +256..." />}
        {method === "Card" && <Input testId="pay-card" label="Card (last 4)" value={card} onChange={setCard} placeholder="•••• 4242" />}
        {(!isMoMo || !pushMode) && (
          <>
            <Input testId="pay-reference" label="Transaction ID / receipt reference *" value={reference} onChange={setReference} placeholder="MTN-Ref, Airtel-Ref, POS receipt no." />
            <Input testId="pay-signature" label="Cashier electronic signature *" value={signature} onChange={setSignature} placeholder="Type your full name" />
            <div className="text-[11px] text-slate-500 mt-1">Signed & timestamped when submitted for validation.</div>
          </>
        )}
        <Input testId="pay-amount" label="Amount (UGX) *" value={amount} onChange={setAmount} />
        {isMoMo && pushMode && (
          <div className="text-[11px] text-slate-500 mt-2">A payment prompt will be pushed to the customer's phone. They enter their MoMo PIN to approve — the invoice auto-marks paid on confirmation.</div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="pay-submit" onClick={submit} className="px-5 h-10 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold">{isMoMo && pushMode ? "Send push to phone" : "Confirm payment"}</button>
        </div>
      </div>
    </div>
  );
}

function Input({ testId, label, value, onChange, placeholder }) {
  return (
    <label className="block mb-2"><span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <input data-testid={testId} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular" />
    </label>
  );
}

function NewInvoiceModal({ onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [items, setItems] = useState([{ description: "Consultation", qty: 1, unit_price: 50000, category: "Consultation" }]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);

  useEffect(()=>{ if (q.length<2){setResults([]);return;} api.get(`/patients?q=${encodeURIComponent(q)}&limit=6`).then(r=>setResults(r.data||[])); }, [q]);

  const addLine = () => setItems([...items, { description: "", qty: 1, unit_price: 0, category: "Other" }]);
  const rmLine = (i) => setItems(items.filter((_,idx)=>idx!==i));
  const upd = (i, k, v) => setItems(items.map((it,idx)=>idx===i?{...it,[k]:v}:it));
  const subtotal = items.reduce((a,i)=>a+Number(i.qty||0)*Number(i.unit_price||0),0);
  const total = Math.max(0, subtotal - Number(discount||0) + Number(tax||0));

  const submit = async () => {
    if (!patient) return toast.error("Select a patient");
    if (!items.length) return toast.error("Add at least one line");
    try {
      await api.post("/invoices", { patient_id: patient.patient_id, items, discount: Number(discount), tax: Number(tax) });
      toast.success("Invoice created"); onDone();
    } catch { toast.error("Failed to create invoice"); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="new-invoice-modal">
        <div className="font-semibold text-slate-900 mb-4">New invoice</div>
        {!patient ? (
          <div>
            <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="ni-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search patient by name, Patient ID, phone…" autoFocus className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="mt-2 divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {results.map(p => (
                <button key={p.patient_id} onClick={()=>setPatient(p)} data-testid={`ni-pick-${p.patient_id}`} className="w-full text-left p-2 hover:bg-slate-50 rounded">
                  <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
                  <div className="text-xs text-slate-500">{p.ths_id} · {p.phone_primary}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between mb-3">
              <div><div className="font-medium text-sm">{patient.first_name} {patient.last_name}</div><div className="text-xs text-slate-500 tabular">{patient.ths_id}</div></div>
              <button onClick={()=>setPatient(null)} className="text-xs text-slate-500 hover:text-slate-800">Change</button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`ni-line-${i}`}>
                  <select value={it.category} onChange={e=>upd(i,'category',e.target.value)} className="col-span-3 h-10 px-2 rounded-lg border border-slate-200 text-xs bg-white">
                    {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                  <input placeholder="Description" value={it.description} onChange={e=>upd(i,'description',e.target.value)} className="col-span-4 h-10 px-2 rounded-lg border border-slate-200 text-sm" />
                  <input placeholder="Qty" type="number" value={it.qty} onChange={e=>upd(i,'qty',e.target.value)} className="col-span-2 h-10 px-2 rounded-lg border border-slate-200 text-sm tabular" />
                  <input placeholder="UGX" type="number" value={it.unit_price} onChange={e=>upd(i,'unit_price',e.target.value)} className="col-span-2 h-10 px-2 rounded-lg border border-slate-200 text-sm tabular" />
                  <button onClick={()=>rmLine(i)} className="col-span-1 h-10 rounded-lg text-slate-400 hover:text-[#B71C1C] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button data-testid="ni-add-line" onClick={addLine} className="text-sm text-[#1565C0] font-medium">+ Add line</button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Input testId="ni-discount" label="Discount (UGX)" value={discount} onChange={setDiscount} />
              <Input testId="ni-tax" label="Tax (UGX)" value={tax} onChange={setTax} />
            </div>
            <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm tabular">
              <div className="flex justify-between"><span>Subtotal</span><span>{UGX(subtotal)}</span></div>
              <div className="flex justify-between text-[#B71C1C]"><span>Discount</span><span>-{UGX(discount)}</span></div>
              <div className="flex justify-between"><span>Tax</span><span>+{UGX(tax)}</span></div>
              <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-2"><span>Total</span><span>{UGX(total)}</span></div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
              <button data-testid="ni-submit" onClick={submit} className="px-5 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Create invoice</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
